import { useId, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CODE_LABELS, sectionCodeNames, sectionCodes, sectionNeeds } from './cloudReportModel';
import { REPORT_GROUPS } from './reportCodes';
import { ReportStatusNotice } from './CloudEvalReport';

/**
 * The scoring form on cloud 5L eval pages. Same look and tabs as the legacy
 * ResultsSubmissionForm, but it has no state of its own: it shows and edits
 * the page's shared report (useCloudReport), so its codes and summary are
 * exactly what the Report tab shows. Values come from the generated report
 * (null shows "Needs choice", never a default), options are Eric's V3 key,
 * and changing a code re-keys that section's paragraph. Saving PATCHes the
 * report through vasi-backend; nothing goes to evaluation_results or
 * summary_templates.
 */

const FORM_TABS = [
  { id: 'caution', name: 'Caution Statement' },
  ...REPORT_GROUPS.map((group) => ({ id: group.id, name: group.label })),
  { id: 'review', name: 'Review & Save' },
];

const FIELD_LABELS = {
  cs: 'Caution Statement',
  sa: 'Sexual Attraction',
  da: 'Data',
  hs: 'High Sexual',
  d: 'Discriminatory',
};

const selectClass = 'w-full rounded-md border shadow-sm py-2 px-3 focus:outline-none sm:text-sm';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function isMissing(value) {
  return value === null || value === undefined;
}

function optionLabel(name, value) {
  if (isMissing(value)) return 'Needs choice';
  return `${CODE_LABELS[name]?.[value] ?? 'Unknown'} (${value})`;
}

function NeedsChoice() {
  return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">Needs choice</span>;
}

function CodeField({ tabName, name, value, onChange }) {
  const id = useId();
  const needsChoice = isMissing(value);
  return (
    <div>
      <label htmlFor={id} className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700">
        {`${FIELD_LABELS[name]} (${name.toUpperCase()})`}
        {needsChoice && <NeedsChoice />}
      </label>
      <select
        id={id}
        aria-label={`${tabName} ${FIELD_LABELS[name]}`}
        className={classNames(
          selectClass,
          needsChoice
            ? 'border-amber-400 bg-amber-50 focus:ring-amber-500 focus:border-amber-500'
            : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
        )}
        value={needsChoice ? '' : String(value)}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      >
        <option value="">Select...</option>
        {Object.entries(CODE_LABELS[name]).map(([optionId, text]) => (
          <option key={optionId} value={optionId}>{`${text} (${optionId})`}</option>
        ))}
      </select>
    </div>
  );
}

function ParagraphPreview({ section }) {
  let body;
  if (section.text?.trim()) {
    body = <p className="whitespace-pre-line text-sm text-gray-700">{section.text}</p>;
  } else if (!section.key) {
    body = <p className="text-sm italic text-gray-500">Choose every code above to pick a paragraph.</p>;
  } else {
    body = <p className="text-sm italic text-amber-800">{`No template paragraph for ${section.key}`}</p>;
  }
  return (
    <div className="mt-6 border-t pt-4" aria-label={`${section.heading} report paragraph`} role="region">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">Report paragraph</span>
        <span className="text-xs text-gray-500">{section.key ?? 'No key yet'}</span>
      </div>
      {body}
      <p className="mt-1 text-xs text-gray-500">
        {section.state === 'edited'
          ? 'Edited for this report only, so code changes will not replace it. Change the wording on the Report tab.'
          : 'Change the wording on the Report tab.'}
      </p>
    </div>
  );
}

export default function CloudScoringForm({ reportState }) {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { status, draft, dirty, saving, message, setCode, setSummaryText, save } = reportState;

  if (status !== 'ready') {
    return <div className="mt-4"><ReportStatusNotice status={status} /></div>;
  }

  const codes = draft.codes;
  const sectionFor = (id) => draft.sections.find((section) => section.id === id);
  const isTabComplete = (tabId) => tabId === 'review' || sectionNeeds(codes, tabId).length === 0;
  const incomplete = FORM_TABS.filter((tab) => !isTabComplete(tab.id));
  const currentTab = FORM_TABS[selectedTabIndex];

  function renderCodesTab(tab) {
    const scored = sectionCodes(codes, tab.id);
    return (
      <div className="bg-white rounded-md p-4">
        <h4 className="text-base font-medium text-gray-900 mb-4">{tab.name}</h4>
        <div className={tab.id === 'caution' ? 'max-w-md' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
          {sectionCodeNames(tab.id).map((name) => (
            <CodeField
              key={name}
              name={name}
              tabName={tab.name}
              value={scored[name]}
              onChange={(value) => setCode(tab.id, name, value)}
            />
          ))}
        </div>
        <ParagraphPreview section={sectionFor(tab.id)} />
      </div>
    );
  }

  function renderReview() {
    return (
      <div className="bg-white rounded-md p-4">
        <h4 className="text-base font-medium text-gray-900 mb-4">Review and Save</h4>
        <p className="text-sm text-gray-600 mb-4">
          These are the report&apos;s codes. Saving updates the report on the Report tab too.
        </p>

        <div className="mb-6 overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Category</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">SA</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">DA</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">HS</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">D</th>
                <th className="px-3 py-2 text-center font-medium text-gray-700 border-b"></th>
              </tr>
            </thead>
            <tbody>
              {FORM_TABS.slice(0, -1).map((tab, index) => {
                const scored = sectionCodes(codes, tab.id);
                const names = sectionCodeNames(tab.id);
                return (
                  <tr key={tab.id} className="border-b">
                    <td className="px-3 py-2 font-medium text-gray-900">{tab.name}</td>
                    {tab.id === 'caution' ? (
                      <td colSpan={4} className="px-3 py-2 text-gray-600">{optionLabel('cs', scored.cs)}</td>
                    ) : (
                      ['sa', 'da', 'hs', 'd'].map((name) => (
                        <td key={name} className="px-3 py-2 text-gray-600">
                          {names.includes(name) ? optionLabel(name, scored[name]) : ''}
                        </td>
                      ))
                    )}
                    <td className="px-3 py-2 text-center">
                      {isTabComplete(tab.id) ? (
                        <CheckCircleIcon className="h-4 w-4 text-green-500 inline" />
                      ) : (
                        <button type="button" className="text-xs text-indigo-600 hover:text-indigo-800" onClick={() => setSelectedTabIndex(index)}>Edit</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6 border-t pt-6">
          <label htmlFor="cloud-scoring-summary" className="block text-sm font-medium text-gray-900 mb-2">
            Summary Statement
          </label>
          <p className="mb-2 text-sm text-gray-500">This is the report&apos;s summary; it shows on the Report tab too.</p>
          <textarea
            id="cloud-scoring-summary"
            rows={6}
            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={draft.summary.text}
            onChange={(event) => setSummaryText(event.target.value)}
            placeholder="Enter the summary statement for this evaluation..."
          />
        </div>

        {incomplete.length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm mt-4">
            {`Still needs codes: ${incomplete.map((tab) => tab.name).join(', ')}. You can save now and finish later.`}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex bg-white rounded-lg shadow overflow-hidden min-h-[500px]">
        <div className="w-64 bg-gray-50 border-r border-gray-200">
          <nav className="flex flex-col h-full py-4 sticky top-0">
            {FORM_TABS.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSelectedTabIndex(index)}
                className={classNames(
                  selectedTabIndex === index
                    ? 'bg-indigo-50 border-l-4 border-indigo-500 text-indigo-700'
                    : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  'flex items-center px-3 py-3 text-sm font-medium'
                )}
              >
                <span className="truncate">{tab.name}</span>
                {isTabComplete(tab.id) && (
                  <CheckCircleIcon className="ml-2 h-4 w-4 text-green-500" aria-hidden="true" />
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 p-8">
          {currentTab.id === 'review' ? renderReview() : renderCodesTab(currentTab)}

          {message && (
            <p className={classNames('mt-4 text-sm', message.tone === 'error' ? 'text-red-700' : 'text-green-700')} role="status">
              {message.text}
            </p>
          )}

          <div className="mt-8 flex items-center justify-between border-t pt-4">
            <button
              type="button"
              onClick={() => setSelectedTabIndex(Math.max(0, selectedTabIndex - 1))}
              disabled={selectedTabIndex === 0}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${selectedTabIndex === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            {dirty && <span className="text-sm font-medium text-amber-700">Unsaved changes</span>}

            {selectedTabIndex < FORM_TABS.length - 1 ? (
              <button
                type="button"
                onClick={() => setSelectedTabIndex(selectedTabIndex + 1)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Next
                <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${saving || !dirty ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`}
              >
                {saving ? 'Saving...' : 'Save scores'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
